import { NavBar } from '../components/NavBar';
import Hero from '../components/Hero';
import { LogoMarquee } from '../components/LogoMarquee';
import Footer from '../components/Footer';
import { LazyGlobe3D, LazyWorldMap, LazySecurityViz, LazyOnRampViz, LazyOffRampViz } from '../components/LazyThreeComponents';
import { Card } from '../components/ui/card';
import { Zap, Globe, Shield, Layers, ArrowUpRight, ArrowDownLeft, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SectionNav from '../components/SectionNav';
import { useCryptoPrices } from '../hooks/useCryptoPrices';

const indexSections = [
  { id: 'hero', label: 'Home' },
  { id: 'infrastructure', label: 'Infrastructure' },
  { id: 'conversion', label: 'Conversion' },
];

const Index = () => {
  const navigate = useNavigate();
  const { prices, isLoading } = useCryptoPrices(['BTC', 'ETH']);
  
  // Calculate live conversion amounts
  const btcPrice = prices['BTC']?.usd_price || 42500;
  const ethPrice = prices['ETH']?.usd_price || 2850;
  const btcChange = prices['BTC']?.price_change_24h || 0;
  const ethChange = prices['ETH']?.price_change_24h || 0;
  
  const btcAmount = (1000 / btcPrice).toFixed(6);
  const ethValue = (1.5 * ethPrice).toFixed(0);

  return (
    <div className="min-h-screen bg-black">
      {/* Skip link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-white focus:text-black focus:rounded-md focus:outline-none"
      >
        Skip to main content
      </a>
      
      <NavBar />
      <SectionNav sections={indexSections} />
      
      <main id="main-content">
        <section id="hero">
          <Hero />
        </section>

        {/* Logo Marquee */}
        <LogoMarquee />

      {/* Infrastructure Section */}
      <section 
        id="infrastructure"
        className="py-24 px-6 bg-black relative overflow-hidden" 
        aria-labelledby="infrastructure-heading"
        style={{ contentVisibility: 'auto', containIntrinsicSize: '0 900px' }}
      >
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" aria-hidden="true"></div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 id="infrastructure-heading" className="text-4xl md:text-5xl font-bold text-white leading-tight">
              Full B2B On-Ramp, Off-Ramp &<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">Crypto Payments Infrastructure.</span>
            </h2>
            <p className="text-lg text-slate-400">
              We remove all the friction typically associated with exchanges and legacy payment flows. Compliance, KYC, processing, and delivery — all handled in the background.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Unified Settlement Engine */}
            <Card className="p-8 bg-white/[0.02] backdrop-blur-xl border-white/10 hover:border-blue-500/30 transition-all duration-300 group relative overflow-hidden shadow-2xl shadow-blue-500/10">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true"></div>
              {/* Card spotlight effect */}
              <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-blue-500/15 rounded-full blur-[100px]" aria-hidden="true"></div>
              <div className="relative z-10 space-y-6">
                <div className="flex items-start justify-between">
                  <div className="p-4 rounded-full bg-blue-500/10 text-blue-400 ring-1 ring-blue-500/20 backdrop-blur-sm">
                    <Zap size={24} aria-hidden="true" />
                  </div>
                  <div className="flex gap-2">
                    <div className="px-3 py-1 rounded-full bg-transparent backdrop-blur-sm border border-blue-500/30 text-blue-400 text-xs font-medium">
                      RAMP: ACTIVE
                    </div>
                    <div className="px-3 py-1 rounded-full bg-transparent backdrop-blur-sm border border-green-500/30 text-green-400 text-xs font-medium">
                      L2: READY
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Unified Settlement Engine</h3>
                  <p className="text-slate-400 leading-relaxed">
                    Stop waiting days for funds to clear. Instant settlement between fiat and crypto rails with real-time monitoring and automated routing.
                  </p>
                </div>
                <div className="pt-4 -mx-8 -mb-8">
                  <LazyGlobe3D />
                </div>
              </div>
            </Card>

            {/* 150+ Countries */}
            <Card className="p-8 bg-white/[0.02] backdrop-blur-xl border-white/10 hover:border-purple-500/30 transition-all duration-300 group relative overflow-hidden shadow-2xl shadow-purple-500/10">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true"></div>
              {/* Card spotlight effect */}
              <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-purple-500/15 rounded-full blur-[100px]" aria-hidden="true"></div>
              <div className="relative z-10 space-y-6">
                <div className="p-4 rounded-full bg-purple-500/10 text-purple-400 ring-1 ring-purple-500/20 w-fit backdrop-blur-sm">
                  <Globe size={24} aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">150+ Countries</h3>
                  <p className="text-slate-400 leading-relaxed">
                    Local payment methods for borderless users and high-growth digital economies. Targeting global markets with localized solutions.
                  </p>
                </div>
                <div className="pt-4 -mx-8 -mb-8">
                  <LazyWorldMap />
                </div>
              </div>
            </Card>

            {/* Bank-Grade Security */}
            <Card className="p-8 bg-white/[0.02] backdrop-blur-xl border-white/10 hover:border-green-500/30 transition-all duration-300 group relative overflow-hidden shadow-2xl shadow-green-500/10">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true"></div>
              {/* Card spotlight effect */}
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-green-500/15 rounded-full blur-[100px]" aria-hidden="true"></div>
              <div className="relative z-10 space-y-6">
                <div className="p-4 rounded-full bg-green-500/10 text-green-400 ring-1 ring-green-500/20 w-fit backdrop-blur-sm">
                  <Shield size={24} aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Bank-Grade Security</h3>
                  <p className="text-slate-400 leading-relaxed">
                    Fully regulated, SOC2 compliant, AES-256 encrypted. Compliance, KYC, and processing handled seamlessly in the background.
                  </p>
                </div>
                <div className="pt-4 -mx-8 -mb-8">
                  <LazySecurityViz />
                </div>
              </div>
            </Card>

            {/* Developer First API */}
            <Card className="p-8 bg-white/[0.02] backdrop-blur-xl border-white/10 hover:border-slate-500/30 transition-all duration-300 group relative overflow-hidden shadow-2xl shadow-slate-500/10">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true"></div>
              {/* Card spotlight effect */}
              <div className="absolute -top-24 -left-24 w-64 h-64 bg-slate-500/15 rounded-full blur-[100px]" aria-hidden="true"></div>
              <div className="relative z-10 space-y-6">
                <div className="p-4 rounded-full bg-slate-500/10 text-slate-400 ring-1 ring-slate-500/20 w-fit backdrop-blur-sm">
                  <Layers size={24} aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Embed NowRamp Anywhere</h3>
                  <p className="text-slate-400 leading-relaxed">
                    Integrate directly into payment pages, wallet interfaces, cashier modules, e-commerce checkout, and mobile apps.
                  </p>
                </div>
                <div className="pt-4">
                  {/* macOS-style Code Window */}
                  <div className="bg-transparent backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden">
                    {/* Window Controls */}
                    <div className="flex items-center gap-2 px-4 py-3 bg-white/[0.02] backdrop-blur-sm border-b border-white/10">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    {/* Code Content */}
                    <pre className="text-sm p-6 leading-relaxed overflow-x-auto">
                      <code>
                        <span className="text-purple-400">const</span> <span className="text-white">ramp</span> <span className="text-slate-400">=</span> <span className="text-purple-400">new</span> <span className="text-blue-400">NowRamp</span><span className="text-slate-400">{'({'}</span>
{'\n'}  <span className="text-white">apiKey</span><span className="text-slate-400">:</span> <span className="text-green-400">'pk_live_...'</span><span className="text-slate-400">,</span>
{'\n'}  <span className="text-white">environment</span><span className="text-slate-400">:</span> <span className="text-green-400">'prod'</span>
{'\n'}<span className="text-slate-400">{'});'}</span>
{'\n'}
{'\n'}<span className="text-slate-500">// Initialize widget</span>
{'\n'}<span className="text-white">ramp</span><span className="text-slate-400">.</span><span className="text-blue-400">open</span><span className="text-slate-400">();</span>
                      </code>
                    </pre>
                  </div>
                  <button 
                    onClick={() => navigate('/docs')}
                    className="mt-4 text-blue-400 hover:text-blue-300 text-sm font-medium flex items-center gap-2 transition-colors"
                  >
                    Read Documentation
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Seamless Conversion Section */}
      <section 
        id="conversion"
        className="py-24 px-6 bg-black relative overflow-hidden" 
        aria-labelledby="conversion-heading"
        style={{ contentVisibility: 'auto', containIntrinsicSize: '0 1000px' }}
      >
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" aria-hidden="true"></div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 id="conversion-heading" className="text-4xl md:text-5xl font-bold text-white leading-tight">
              Seamless Conversion. <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-400">Both Ways.</span>
            </h2>
            <p className="text-lg text-slate-400">
              For individuals looking to buy crypto instantly, or businesses wanting to accept crypto from card holders — NowRamp delivers.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* On-Ramp Card */}
            <Card className="p-8 bg-white/[0.02] backdrop-blur-xl border-white/10 hover:border-blue-500/30 transition-all duration-300 group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative z-10 space-y-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-medium mb-4 border border-blue-500/20">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                      ON-RAMP
                    </div>
                    <h3 className="text-3xl font-bold text-white mb-2">Fiat → Crypto</h3>
                    <p className="text-slate-400 text-sm">Buy Bitcoin, Ethereum, USDT within seconds using cards, bank transfers, Apple Pay</p>
                  </div>
                  <ArrowDownLeft className="text-blue-400" size={32} />
                </div>

                <div className="space-y-4 pt-4">
                  <div className="-mx-8">
                    <LazyOnRampViz />
                  </div>
                  
                  <div className="p-6 rounded-xl bg-transparent backdrop-blur-sm border border-white/[0.08] shadow-xl shadow-blue-500/5">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-slate-400 text-sm">You Pay</span>
                      <span className="text-2xl font-bold text-white">$1,000</span>
                    </div>
                    <div className="flex items-center justify-center py-4">
                      <div className="p-2 rounded-full bg-blue-500/10 backdrop-blur-sm">
                        <ArrowDownLeft className="text-blue-400" size={24} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-sm">You Receive</span>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-blue-400">{btcAmount} BTC</span>
                        <span className={`ml-2 text-sm ${btcChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          ({btcChange >= 0 ? '+' : ''}{btcChange.toFixed(2)}%)
                        </span>
                      </div>
                    </div>
                  </div>

                  <button 
                    onClick={() => navigate('/buy')}
                    className="w-full py-4 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    Buy Crypto Now
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            </Card>

            {/* Off-Ramp Card */}
            <Card className="p-8 bg-white/[0.02] backdrop-blur-xl border-white/10 hover:border-green-500/30 transition-all duration-300 group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative z-10 space-y-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 text-green-400 text-xs font-medium mb-4 border border-green-500/20">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
                      OFF-RAMP
                    </div>
                    <h3 className="text-3xl font-bold text-white mb-2">Crypto → Fiat</h3>
                    <p className="text-slate-400 text-sm">Accept card payments that convert to crypto. Reduce chargebacks. Direct bank settlement.</p>
                  </div>
                  <ArrowUpRight className="text-green-400" size={32} />
                </div>

                <div className="space-y-4 pt-4">
                  <div className="-mx-8">
                    <LazyOffRampViz />
                  </div>
                  
                  <div className="p-6 rounded-xl bg-transparent backdrop-blur-sm border border-white/[0.08] shadow-xl shadow-green-500/5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400 text-sm">You Sell</span>
                        <span className={`text-xs ${ethChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          ({ethChange >= 0 ? '+' : ''}{ethChange.toFixed(2)}%)
                        </span>
                      </div>
                      <span className="text-2xl font-bold text-white">1.5 ETH</span>
                    </div>
                    <div className="flex items-center justify-center py-4">
                      <div className="p-2 rounded-full bg-green-500/10 backdrop-blur-sm">
                        <ArrowUpRight className="text-green-400" size={24} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-sm">You Receive</span>
                      <span className="text-2xl font-bold text-green-400">${Number(ethValue).toLocaleString()}</span>
                    </div>
                  </div>

                  <button 
                    onClick={() => navigate('/buy')}
                    className="w-full py-4 px-6 rounded-xl bg-green-800 hover:bg-green-900 text-white font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    Sell Crypto Now
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

        <Footer />
      </main>
    </div>
  );
};

export default Index;
